/**
 * Shared utilities for chat data operations
 * Used by useAIChatModeConfig, useAIDataCollection, and useUnifiedChat hooks
 */

import { supabase } from '@/integrations/supabase/client';
import { fetchFieldsDictionary } from '@/services/chatbotDataService';
import { FieldDictionary } from '@/types/fieldsDictionary';
import { getPiiChatUserInformation, getSessionPiiToken } from '@/utils/piiUtils';

/**
 * User info item type - represents a field with its value
 */
export interface UserInfoItem {
  [fieldKey: string]: string;
}

/**
 * Result type for getFieldsDictionaryAndUserInfo function
 */
export interface FieldsAndUserInfoResult {
  fieldsDictionary: FieldDictionary[];
  userInfo: UserInfoItem[];
  piiToken: string | null;
}

/**
 * Get PII token for conversation using multiple fallback methods
 */
export async function getPiiTokenForConversation(
  conversationId: string,
  chatbotId: string,
  sessionId: string
): Promise<string | null> {
  try {
    // Method 1: Try to get from sessionStorage
    if (typeof window !== 'undefined') {
      const sessionToken = getSessionPiiToken(chatbotId, sessionId);
      if (sessionToken) {
        console.log('🔑 PII Token - Found in sessionStorage', {
          tokenPreview: sessionToken.slice(0, 8) + '...'
        });
        return sessionToken;
      }

      // Method 2: Try to get from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('pii') || urlParams.get('pii_token');
      if (urlToken) {
        console.log('🔑 PII Token - Found in URL', {
          tokenPreview: urlToken.slice(0, 8) + '...'
        });
        return urlToken;
      }
    }

    // Method 3: Try to get from conversation metadata
    const { data: conversation, error: convError } = await supabase
      .from('conversations')
      .select('metadata')
      .eq('id', conversationId)
      .single();

    if (!convError && conversation?.metadata) {
      const metadata = conversation.metadata as any;
      const metadataToken = metadata.pii_token || metadata.piiToken;
      if (metadataToken) {
        console.log('🔑 PII Token - Found in conversation metadata', {
          tokenPreview: metadataToken.slice(0, 8) + '...'
        });
        return metadataToken;
      }
    }

    // Method 4: Try to find PII token by searching for session_id in PII data
    if (sessionId) {
      const { data: piiTokensData, error: piiError } = await supabase
        .from('pii_tokens')
        .select(`
          token,
          pii_data!inner(
            data
          )
        `)
        .contains('pii_data.data', { _session_id: sessionId })
        .order('created_at', { ascending: false })
        .limit(1);

      if (!piiError && piiTokensData && piiTokensData.length > 0) {
        const foundToken = piiTokensData[0].token;
        console.log('🔑 PII Token - Found in database by session_id', {
          tokenPreview: foundToken.slice(0, 8) + '...'
        });
        return foundToken;
      }
    }

    console.log('⚠️ PII Token - Not found for conversation', {
      conversationId,
      chatbotId,
      sessionId
    });
    return null;
  } catch (error) {
    console.error('❌ Error getting PII token for conversation:', error);
    return null;
  }
}

/**
 * Shared utility function to fetch fields dictionary and user info
 * Can be used by useAIChatModeConfig and useAIDataCollection hooks
 * 
 * @param conversationId - The conversation ID
 * @param chatbotId - The chatbot ID
 * @param sessionId - The session ID
 * @returns Object containing fieldsDictionary, userInfo, and piiToken
 */
export async function getFieldsDictionaryAndUserInfo(
  conversationId?: string,
  chatbotId?: string,
  sessionId?: string
): Promise<FieldsAndUserInfoResult> {
  // Fetch fields dictionary
  const fieldsDictionary = await fetchFieldsDictionary();
  
  // Initialize userInfo array
  const userInfo: UserInfoItem[] = [];
  let piiToken: string | null = null;

  // Try to get PII token and user info if we have all required IDs
  if (conversationId && chatbotId && sessionId) {
    piiToken = await getPiiTokenForConversation(conversationId, chatbotId, sessionId);
    
    if (piiToken) {
      const { success, data: user } = await getPiiChatUserInformation(conversationId, piiToken);
      if (success && user?.fields) {
        Object.entries(user.fields).forEach(([key, value]) => {
          userInfo.push({ [key]: (value as any)?.value || '' });
        });
      }
    }
  }

  return {
    fieldsDictionary,
    userInfo,
    piiToken
  };
}





